import { MAKER } from './catalog';
import { SCHEMA_VERSION, today } from './project';
import { newId } from './tagging';
import type { Page, Project, Pt, Sym, SymProps } from './types';
import { t as $t } from './i18n';


interface SymOpts {
  rot?: number;
  tag?: string;
  desc?: string;
  part?: string;
  props?: SymProps;
}

/** Example project: direct-on-line pump starter across three pages. */
export function sampleProject(): Project {
  const pr: Project = {
    schema: SCHEMA_VERSION, name: $t("Pompa P-101 motor kumandası"), customer: $t("Örnek Tesis · Arıtma binası"),
    author: $t("Proje mühendisi"), date: today(), fn: 'P101', loc: 'MCC1', pages: [],
  };
  const page = (desc: string) => {
    const p: Page = { id: newId(), desc, syms: [], wires: [] };
    pr.pages.push(p);
    return p;
  };
  const S = (p: Page, type: string, x: number, y: number, o: SymOpts = {}) => {
    const s: Sym = {
      id: newId(), type, x, y, rot: o.rot || 0, tag: o.tag || '', desc: o.desc || '', part: o.part || '',
      maker: o.part ? MAKER : '', props: o.props || {},
    };
    p.syms.push(s);
    return s;
  };
  const W = (p: Page, pts: Pt[], cs = '', col = '') => p.wires.push({ id: newId(), pts, cs, col });

  // 1 — power circuit
  const p1 = page($t("Güç devresi · pompa motoru M1"));
  ([['L1', 24, 'L'], ['L2', 32, 'L'], ['L3', 40, 'L'], ['N', 48, 'N'], ['PE', 56, 'PE']] as const).forEach(([n, y, t]) => {
    S(p1, 'pot', 28, y, { props: { name: n, ptype: t } });
    W(p1, [[28, y], [248, y]], '2.5', t === 'N' ? 'BU' : t === 'PE' ? 'GNYE' : 'BK');
  });
  S(p1, 'text', 100, 19, { props: { text: '3/N/PE ~ 400/230 V 50 Hz', size: 2.5 } });
  S(p1, 'cb3', 72, 72, { tag: 'Q1', desc: $t("Motor koruma"), part: 'MKS-4.0' });
  ([[72, 24], [80, 32], [88, 40]] as const).forEach(([x, y]) => W(p1, [[x, y], [x, 72]], '2.5', 'BK'));
  S(p1, 'k3', 72, 108, { tag: 'K1' });
  [72, 80, 88].forEach((x) => W(p1, [[x, 96], [x, 108]], '2.5', 'BK'));
  S(p1, 'ol3', 72, 140, { tag: 'F2', desc: $t("Aşırı yük"), part: 'TAR-2.5-4' });
  [72, 80, 88].forEach((x) => W(p1, [[x, 128], [x, 140]], '2.5', 'BK'));
  ([['1', 72], ['2', 80], ['3', 88], ['4', 96]] as const).forEach(([n, x]) =>
    S(p1, 'term', x, 172, { tag: 'X1', part: n === '4' ? 'KL-4-PE' : 'KL-2.5', props: { pinNo: n } }),
  );
  [72, 80, 88].forEach((x) => W(p1, [[x, 160], [x, 172]], '2.5', 'BK'));
  W(p1, [[120, 56], [120, 166], [96, 166], [96, 172]], '2.5', 'GNYE');
  S(p1, 'motor3', 72, 196, { tag: 'M1', desc: $t("Pompa P-101 · 1,5 kW"), part: 'MOT-1.5-4P' });
  [72, 80, 88, 96].forEach((x) => W(p1, [[x, 180], [x, 196]], '2.5', x === 96 ? 'GNYE' : 'BK'));
  S(p1, 'cable', 64, 188, { tag: 'W1', props: { len: 40, cableType: 'NYY-J', cores: '1,2,3,PE', cs: '1.5', length: '25' } });
  S(p1, 'cb1', 208, 72, { tag: 'F3', desc: $t("Kumanda sigortası"), part: 'OS-C6-1P' });
  W(p1, [[208, 24], [208, 72]], '1.5', 'BK');
  W(p1, [[208, 96], [208, 112]], '1.5', 'RD');
  S(p1, 'brk', 208, 112, { props: { name: '1L', ptype: 'L' } });
  W(p1, [[232, 48], [232, 112]], '1.5', 'BU');
  S(p1, 'brk', 232, 112, { props: { name: '1N', ptype: 'N' } });

  // 2 — 230 V control circuit
  const p2 = page($t("Kumanda devresi · 230 V AC"));
  S(p2, 'brk', 32, 24, { rot: 1, props: { name: '1L', ptype: 'L' } });
  W(p2, [[32, 24], [300, 24]], '1.5', 'RD');
  S(p2, 'brk', 32, 224, { rot: 1, props: { name: '1N', ptype: 'N' } });
  W(p2, [[32, 224], [300, 224]], '1.5', 'BU');
  W(p2, [[72, 24], [72, 40]]);
  S(p2, 'nc', 72, 40, { tag: 'F2' });
  W(p2, [[72, 56], [72, 68]]);
  S(p2, 'pb_nc', 72, 68, { tag: 'S0', desc: 'Stop', part: 'BT-KR-1NC' });
  W(p2, [[72, 84], [72, 100]]);
  S(p2, 'pb_no', 72, 100, { tag: 'S1', desc: 'Start', part: 'BT-YS-1NO' });
  S(p2, 'no', 104, 100, { tag: 'K1' });
  W(p2, [[72, 92], [104, 92], [104, 100]]);
  W(p2, [[104, 116], [104, 124], [72, 124]]);
  W(p2, [[72, 116], [72, 140]]);
  S(p2, 'coil', 72, 140, { tag: 'K1', desc: $t("Motor kontaktörü"), part: 'KT-09-230AC' });
  W(p2, [[72, 160], [72, 224]]);
  W(p2, [[152, 24], [152, 56]]);
  S(p2, 'no', 152, 56, { tag: 'K1' });
  W(p2, [[152, 72], [152, 140]]);
  S(p2, 'lamp', 152, 140, { tag: 'H1', desc: $t("Çalışıyor"), part: 'SL-230-G' });
  W(p2, [[152, 156], [152, 224]]);
  W(p2, [[192, 24], [192, 56]]);
  S(p2, 'no', 192, 56, { tag: 'F2' });
  W(p2, [[192, 72], [192, 140]]);
  S(p2, 'lamp', 192, 140, { tag: 'H2', desc: $t("Termik arıza"), part: 'SL-230-R' });
  W(p2, [[192, 156], [192, 224]]);

  // 3 — 24 V DC supply and PLC feedback
  const p3 = page($t("24 V DC besleme · PLC geri bildirimleri"));
  S(p3, 'brk', 72, 32, { rot: 2, props: { name: '1L', ptype: 'L' } });
  W(p3, [[72, 32], [72, 48]], '1.5', 'RD');
  S(p3, 'brk', 96, 32, { rot: 2, props: { name: '1N', ptype: 'N' } });
  W(p3, [[96, 32], [96, 40], [80, 40], [80, 48]], '1.5', 'BU');
  S(p3, 'psu', 72, 48, { tag: 'G1', desc: $t('24 V DC besleme'), part: 'GK-24-5' });
  S(p3, 'pot', 48, 96, { props: { name: '+24V', ptype: 'DC+' } });
  W(p3, [[48, 96], [300, 96]], '1', 'DBU');
  S(p3, 'pot', 48, 224, { props: { name: '0V', ptype: 'DC-' } });
  W(p3, [[48, 224], [300, 224]], '1', 'WH');
  W(p3, [[72, 72], [72, 96]], '1', 'DBU');
  W(p3, [[80, 72], [80, 224]], '1', 'WH');
  W(p3, [[152, 96], [152, 112]]);
  S(p3, 'no', 152, 112, { tag: 'K1' });
  W(p3, [[152, 128], [152, 140]]);
  S(p3, 'plcin', 152, 140, { tag: 'A1', desc: $t("K1 çalışıyor"), part: 'PLC-DI16', props: { addr: 'I0.0' } });
  W(p3, [[192, 96], [192, 112]]);
  S(p3, 'no', 192, 112, { tag: 'Q1' });
  W(p3, [[192, 128], [192, 140]]);
  S(p3, 'plcin', 192, 140, { tag: 'A1', desc: $t('Q1 devrede'), part: 'PLC-DI16', props: { addr: 'I0.1' } });
  return pr;
}
